import React, { useEffect, useRef, useState } from 'react';
import { Input, InputProps } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';
import { GmapsAddress, placeToAddress, useGoogleMap } from '/imports/gmaps';
import { strings } from '/imports/strings';

const suggestionsClass = 'suggestions absolute z-1 rounded-input border border-mid bg-panel [&_.prediction]:cursor-pointer [&_.prediction]:px-4 [&_.prediction]:py-1 [&_.prediction:hover]:bg-grey-7';
// Mirrors the converted Input.tsx so the bare <input> below renders identically.
const inputElClass = 'box-border min-h-input rounded-input border border-mid bg-page py-0 pr-10 pl-4 font-sans text-base placeholder:italic focus:[outline:1px_solid_var(--color-primary)] disabled:cursor-not-allowed disabled:bg-grey-8';
const iconStartClass = 'icon-start absolute top-4 left-4';
const clearClass = 'clear absolute top-0 right-5 inline-flex h-full w-4 cursor-pointer';

export interface AddressInputProps extends InputProps<GmapsAddress> {
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  value?: GmapsAddress;
  onInput?(input?: string): void;
  onChange?(address?: GmapsAddress): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  autoCompleteRequest?: Partial<google.maps.places.AutocompletionRequest>;
  inline?: boolean;
}

export const AddressInput = (props: AddressInputProps) => {
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  const inputRef = useRef<HTMLInputElement>(null);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>();
  const [address, setAddress] = useState<Partial<GmapsAddress>>(props.value || {});
  const clearInput = () => {
    props.onClear?.();
    props.onInput?.(undefined);
    const input = inputRef.current;
    if (input) {
      input.value = '';
    }
  };
  const { isLoaded } = useGoogleMap();
  const getSuggestions = async (input: string) => {
    if (!isLoaded) return;
    const options = {
      input,
      ...props.autoCompleteRequest,
    };
    const result = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(options as google.maps.places.AutocompleteRequest);
    setSuggestions(result?.suggestions);
  };
  const placeSelected = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    const placeId = suggestion.placePrediction?.placeId;
    if (!placeId) return clearInput();
    const place = new google.maps.places.Place({ id: placeId });
    await place.fetchFields({ fields: ['addressComponents', 'displayName', 'location'] });
    const address = placeToAddress(place);
    delete address.place;
    setAddress(address);
    setSuggestions(undefined);
    if (inputRef.current) inputRef.current.value = props.inline ? address.formatted : address.line;
  };
  useEffect(() => {
    if (!isLoaded) return;
    address && props.onChange?.(address as GmapsAddress);
  }, [address]);
  return (
    <span id={props.id} className={`input address-input relative flex flex-wrap gap-4 [&.error_input]:border-red-2 ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {
        props.inline ?
          <span className="line relative flex-1">
            {hasStartIcon && <Icon className={iconStartClass} icon={props.icon as any} />}
            <input
              ref={inputRef}
              className={`${inputElClass}${hasStartIcon ? ' has-start-icon pl-10' : ''}`}
              type="text"
              placeholder={props.placeholder || strings.Line()}
              disabled={props.disabled}
              autoFocus={props.autoFocus}
              defaultValue={address?.formatted}
              onChange={(e) => { props.onInput?.(e.target.value); getSuggestions(e.target.value); }}
              onClick={(e) => props.onClick?.(e)}
              onFocus={props.onFocus}
              onBlur={props.onBlur}
              onKeyUp={(e) => {
                props.onKeyPress?.(e);
                if (e.key === 'Enter') {
                  const suggestion = suggestions?.[0];
                  suggestion && placeSelected(suggestion);
                }
              }}
              autoComplete={props.autoComplete}
            />
            {
              props.value !== undefined &&
              !props.disabled &&
              !(props.type === 'color') &&
              <Icon className={clearClass} icon="cancel" onClick={() => clearInput()} /> || null
            }
            {
              suggestions?.length ?
                <div className={suggestionsClass}>
                  {
                    suggestions.map((p) => {
                      return <div key={p.placePrediction?.placeId} className="prediction" onClick={() => placeSelected(p)}>{p.placePrediction?.text.text}</div>;
                    })
                  }
                </div>
                :
                null
            }
          </span>
          :
          <>
            <span className="line relative flex-1">
              {hasStartIcon && <Icon className={iconStartClass} icon={props.icon as any} />}
              <input
                ref={inputRef}
                className={`${inputElClass}${hasStartIcon ? ' has-start-icon pl-10' : ''}`}
                type="text"
                placeholder={props.placeholder || strings.Line()}
                disabled={props.disabled}
                autoFocus={props.autoFocus}
                defaultValue={address?.line}
                onChange={(e) => { props.onInput?.(e.target.value); getSuggestions(e.target.value); }}
                onClick={(e) => props.onClick?.(e)}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                onKeyUp={(e) => {
                  props.onKeyPress?.(e);
                  if (e.key === 'Enter') {
                    const suggestion = suggestions?.[0];
                    suggestion && placeSelected(suggestion);
                  }
                }}
                autoComplete={props.autoComplete}
              />
              {
                props.value !== undefined &&
                !props.disabled &&
                !(props.type === 'color') &&
                <Icon className={clearClass} icon="cancel" onClick={() => clearInput()} /> || null
              }
              {
                suggestions?.length ?
                  <div className={suggestionsClass}>
                    {
                      suggestions.map((p) => {
                        return <div key={p.placePrediction?.placeId} className="prediction" onClick={() => placeSelected(p)}>{p.placePrediction?.text.text}</div>;
                      })
                    }
                  </div>
                  :
                  null
              }
            </span>
            <span className="line2 relative flex-[0.5]">
              <Input value={address?.line2} onChange={(line2) => setAddress({ ...address, line2 })} placeholder={strings.Line2()} />
            </span>
            <span className="break relative h-0 grow shrink basis-full" />
            <span className="city relative flex-1">
              <Input value={address?.city} onChange={(city) => setAddress({ ...address, city })} placeholder={strings.City()} />
            </span>
            <span className="state relative flex-[0.5]">
              <Input value={address?.state} onChange={(state) => setAddress({ ...address, state })} placeholder={strings.State()} />
            </span>
            <span className="zip relative flex-[0.5]">
              <Input value={address?.zipcode} onChange={(zipcode) => setAddress({ ...address, zipcode })} placeholder={strings.Zipcode()} />
            </span>
          </>
      }
    </span>
  );
};